import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { LeaveListItem, Role } from '@lms/shared-types'
import { roleAtLeast } from '@lms/shared-types'
import { Banner } from '../components/primitives/Banner'
import { Modal } from '../components/primitives/Modal'
import { Card } from '../components/primitives/Card'
import { FadeIn } from '../components/primitives/FadeIn'
import { useToast } from '../components/primitives/Toast'
import { ApprovalsTable } from '../components/manager/ApprovalsTable'
import { PaginationControls } from '../components/lists/PaginationControls'
import { EscalateModal } from '../components/manager/EscalateModal'
import { ApiError } from '../api/client'
import { listHrReviewsPage } from '../api/hr-leave-reviews'
import { HrReviewTab } from './hr-leaves-hub/HrReviewTab'
import {
  approveLeave,
  capacityAckRequired,
  type CapacityAckSnapshot,
  listEscalatedByMe,
  listLeaves,
  rejectLeave,
} from '../api/leaves'
import { listPeakPeriods } from '../api/peak-periods'
import { listBlackouts, listOverlappingLeaves } from '../api/blackouts'
import { useSession } from '../auth/auth-context'
import { useEffectiveEmployeeId, useEffectiveRole } from '../components/shell/effective-employee'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'
import { flags } from '../lib/feature-flags'
import { displayName } from '../lib/display-name'
import {
  computeConflictPeers,
  groupConflictClusters,
  type ConflictGroup,
} from '../lib/schedule-conflicts'
import { useManagerPeakConflict } from '../lib/use-manager-peak-conflict'
import {
  detectConflictPairs,
  pickClusterPair,
  pickPeakPeriodForPair,
  type ConflictPair,
} from '../lib/peak-conflict-pair'

// Roles that see the unfiltered, company-wide queue here. Empty by design:
// every role on this page sees only their OWN assigned approvals
// (scope=team) — it's a personal action queue, not an oversight view. HR
// used to get the company-wide scope, but that mismatched the nav badge
// (always scope=team) and confused HR admins reaching it via My team; HR's
// company-wide leave list lives on /hr/leaves instead. Director and Exec
// Director view company data on /director/escalations.
const ALL_SCOPE_ROLES = new Set<Role>([])

// Tab model: For approvals (everything awaiting my decision — direct pending
// PLUS leaves escalated to me) | You escalated (leaves I pushed up the chain,
// awaiting a higher decision) | History. The old standalone Director/Exec
// Escalations page folded into "For approvals" here — one action surface per
// approver. History is the full decision log (approved + rejected); filter
// chips narrow it — the everyday "did it go through?" lookup is the Approved chip.
type TabValue = 'for-approvals' | 'hr-doc-review' | 'you-escalated' | 'history'
type HistoryFilter = 'all' | 'approved' | 'rejected'

// In-session history entry (matches prototype `decisions` map)
interface HistoryEntry {
  leave: LeaveListItem
  decision: 'approved' | 'rejected'
  at: number
}

export function ManagerRequestsPage(): JSX.Element {
  const { user } = useSession()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Back-compat: the Slack DM card used to deep-link to
  // /manager/requests?leave=<uuid> before the dedicated /leave detail page
  // existed. Old DMs are still in inboxes — bounce them to the new URL so
  // the link keeps working without forcing the manager to copy/paste the
  // id manually.
  const search = useSearch({ strict: false }) as Record<string, string | undefined>
  const legacyLeaveId = search['leave']
  useEffect(() => {
    if (legacyLeaveId) {
      void navigate({
        to: '/leave',
        search: { id: legacyLeaveId },
        replace: true,
      })
    }
  }, [legacyLeaveId, navigate])

  // Deep-link context from blackout signals:
  // /manager/requests?blackoutId=<uuid> means the manager arrived here to
  // see leaves overlapping a specific blackout window. Surface that context
  // as a banner + filter the visible list to the matching set so the deep-
  // link lands somewhere useful instead of the unfiltered queue.
  const blackoutFilter = search['blackoutId']
  const blackoutMetaQuery = useQuery({
    queryKey: ['blackouts', 'list'],
    queryFn: listBlackouts,
    enabled: Boolean(blackoutFilter),
    staleTime: 60_000,
  })
  const blackoutOverlapsQuery = useQuery({
    queryKey: ['blackouts', 'overlapping', blackoutFilter],
    queryFn: () => listOverlappingLeaves(blackoutFilter as string),
    enabled: Boolean(blackoutFilter),
    staleTime: 30_000,
  })
  const blackoutContext = useMemo(() => {
    if (!blackoutFilter) return null
    const meta = blackoutMetaQuery.data?.find((b) => b.id === blackoutFilter)
    const overlapIds = new Set(blackoutOverlapsQuery.data?.map((o) => o.leaveId) ?? [])
    return { meta, overlapIds }
  }, [blackoutFilter, blackoutMetaQuery.data, blackoutOverlapsQuery.data])
  const clearBlackoutFilter = useCallback(() => {
    void navigate({ to: '/manager/requests', search: {}, replace: true })
  }, [navigate])
  // Open the shared leave detail page (`/leave?id=`) — used by the Approved /
  // Decided table rows so a manager can drill into any past decision.
  const openLeaveDetail = useCallback(
    (leaveId: string) => {
      void navigate({ to: '/leave', search: { id: leaveId } })
    },
    [navigate],
  )
  const realRole: Role = (user?.role as Role) ?? 'employee'
  const effectiveRole = useEffectiveRole(realRole)
  const effectiveEmployeeId = useEffectiveEmployeeId(user?.id ?? null)
  // Scope follows the EFFECTIVE role: when viewing-as a lower role (e.g. an HR
  // admin viewing-as an HOD), the queue must narrow to that person's team, not
  // the real admin's company-wide scope. Derived (not useState) so it recomputes
  // when the viewed-as identity changes mid-session.
  const canSeeAll = ALL_SCOPE_ROLES.has(effectiveRole)
  // Only director+ receive escalations (escalation terminates at the Exec
  // Director), so only they get the escalated-to-me slice folded into "For
  // approvals". Managers/HOD receive none, so their For-approvals is just their
  // direct pending queue.
  const roleReceivesEscalations = roleAtLeast(effectiveRole, 'director')
  // HR-first review queue as an HR-only tab on this action surface. Visible to
  // HR ops (HR + admin/superadmin, NOT line directors/exec) and only when the
  // feature is on. The manager/employee never sees it — those leaves are
  // excluded from their flow. The api still guards via require_hr.
  const showHrDocReview =
    flags.hrDocumentReview && (effectiveRole === 'hr' || roleAtLeast(effectiveRole, 'admin'))
  // Live count for the tab label. Keyed under ['hr-review-queue', …] so the
  // forward/reject/request-document mutations in HrReviewTab (which invalidate
  // ['hr-review-queue']) refresh this badge too.
  const hrReviewCountQuery = useQuery({
    queryKey: ['hr-review-queue', 'count'],
    queryFn: () => listHrReviewsPage({ page: 1, limit: 1 }).then((r) => r.meta.total),
    enabled: showHrDocReview,
    staleTime: 30_000,
  })
  const hrReviewCount = hrReviewCountQuery.data ?? 0
  // Tab model: For approvals | You escalated | History. Derived from the URL
  // (not local state) so opening a leave detail and closing it (history.back)
  // returns to the same tab instead of snapping back. `setTab` mirrors the
  // choice into the URL with `replace` so tab switches don't stack history
  // entries (Back shouldn't step through tabs). Legacy `?tab=pending`/
  // `?tab=escalated` links (old Slack DMs, bookmarks) map onto the new tabs.
  const rawTab = search['tab']
  const tab: TabValue =
    rawTab === 'hr-doc-review' && showHrDocReview
      ? 'hr-doc-review'
      : rawTab === 'you-escalated' || rawTab === 'escalated'
        ? 'you-escalated'
        : rawTab === 'history'
          ? 'history'
          : 'for-approvals'
  const setTab = useCallback(
    (next: TabValue): void => {
      void navigate({
        to: '/manager/requests',
        search: (prev) => {
          const out: Record<string, string> = {}
          for (const [k, v] of Object.entries(prev)) {
            if (typeof v === 'string' && v !== '') out[k] = v
          }
          // For approvals is the default → drop the param so its URL stays clean.
          if (next === 'for-approvals') delete out['tab']
          else out['tab'] = next
          // Leaving History clears its outcome filter so it doesn't linger.
          if (next !== 'history') delete out['filter']
          return out
        },
        replace: true,
      })
    },
    [navigate],
  )
  // Within History, which decision outcome is shown. Defaults to the full log.
  const rawFilter = search['filter']
  const historyFilter: HistoryFilter =
    rawFilter === 'approved' || rawFilter === 'rejected' ? rawFilter : 'all'
  const setHistoryFilter = useCallback(
    (next: HistoryFilter): void => {
      void navigate({
        to: '/manager/requests',
        search: (prev) => {
          const out: Record<string, string> = {}
          for (const [k, v] of Object.entries(prev)) {
            if (typeof v === 'string' && v !== '') out[k] = v
          }
          if (next === 'all') delete out['filter']
          else out['filter'] = next
          return out
        },
        replace: true,
      })
    },
    [navigate],
  )
  const scope: 'team' | 'all' = canSeeAll ? 'all' : 'team'
  const [rejectTarget, setRejectTarget] = useState<LeaveListItem | null>(null)
  const [rejectReason, setRejectReason] = useState<string>('')
  const [rejectError, setRejectError] = useState<string | null>(null)
  // Over-capacity approve confirm (capacity de-gated 2026-09-24): approving from
  // the queue a leave that puts the team over its cap is allowed, but the
  // manager confirms it first. The snapshot comes from the /approve 409. Same
  // flow the detail page runs, on the queue's inline Approve.
  const [capacityConfirmLeave, setCapacityConfirmLeave] =
    useState<LeaveListItem | null>(null)
  const [capacitySnapshot, setCapacitySnapshot] =
    useState<CapacityAckSnapshot | null>(null)
  const [capacityConfirmError, setCapacityConfirmError] = useState<string | null>(
    null,
  )
  // Required reason captured in the over-capacity confirm modal — saved as the
  // approval note (audit after.reason), same destination as a routine note.
  const [capacityNote, setCapacityNote] = useState('')
  const [capacityBusy, setCapacityBusy] = useState(false)
  const [escalateTarget, setEscalateTarget] = useState<LeaveListItem | null>(null)
  // In-session decision log — mirrors prototype `decisions` state for History tab
  const [historyLog, setHistoryLog] = useState<ReadonlyArray<HistoryEntry>>([])
  const { notify } = useToast()

  // `/api/v1/leaves` is currently offset/limit, not cursor-shaped — see
  // docs/standards/pagination.md retrofit checklist. Two separate fetchers
  // keep the pending and escalated pages in independent TanStack Query caches
  // so switching tabs doesn't lose page state.
  const userPresent = Boolean(user)

  // ── For-approvals list builder ────────────────────────────────────────────
  // "For approvals" is everything awaiting the viewer's own decision:
  // freshly-submitted pending leaves, pending cancellations, AND — for director+
  // (the escalation target) — leaves escalated up to them. This builds the full
  // merged/deduped/sorted/owner-hidden array once; the numbered fetcher slices
  // the current page out of it.
  const buildForApprovalsList = useCallback(async (): Promise<LeaveListItem[]> => {
    if (!userPresent) return []
    const fetches: Array<Promise<LeaveListItem[]>> = [
      listLeaves({ scope, status: 'pending', limit: 200 }),
      // Cancellation requests on already-approved leave — the employee asked to
      // cancel and a manager must decide. Surfaced here so they aren't stranded
      // (previously only reachable via the notification / Slack deep-link). The
      // decision happens on the leave detail page, same as every other row here.
      listLeaves({ scope, status: 'pending_cancellation', limit: 200 }),
    ]
    if (roleReceivesEscalations) {
      // Escalated-to-me inbox. Escalation terminates at the Exec Director, so
      // director+ pull the company-wide escalated queue (scope=all, which their
      // role permits) into their action list — what the old standalone
      // /director/escalations page showed.
      fetches.push(listLeaves({ scope: 'all', status: 'escalated', limit: 200 }))
    }
    const results = await Promise.all(fetches)
    // Dedup by id — a leave has exactly one status, but be defensive.
    const seen = new Set<string>()
    // NOTE: do NOT hide pending rows whose end_date is already in the past.
    // Backdated leave is the normal flow (a late medical-cert submission lands
    // with dates already elapsed), and it's still an unmade decision that the
    // badge + Slack reminder count — hiding it here made the Approvals page
    // disagree with the "N to approve" badge (manager pinged but the table was
    // empty). Surface it so it can actually be actioned.
    const all = results.flat().filter((lv) => {
      if (seen.has(lv.id)) return false
      seen.add(lv.id)
      return true
    })
    // Order the merged union first-come-first-serve — oldest submission first.
    // This is a hard rule from the business owner (2026-08-11): no scoring is
    // applied to queue order. The merged union still needs a client-side sort
    // because it splices three separate server pages together.
    all.sort(comparePendingByOldest)
    // A manager / HOD can't approve their own leave — hide own rows.
    return effectiveEmployeeId
      ? all.filter((l) => l.employee.id !== effectiveEmployeeId)
      : all
  }, [userPresent, scope, effectiveEmployeeId, roleReceivesEscalations])

  // ── You-escalated list builder ────────────────────────────────────────────
  // Leaves the viewer personally pushed up the chain, sourced from the audit
  // log server-side (the leave row records only the target, not the escalating
  // actor). Read-only here — a higher approver owns the decision. Terminal
  // approvers (Exec Director) never escalate up, so this is empty for them.
  const buildEscalatedList = useCallback(async (): Promise<LeaveListItem[]> => {
    if (!userPresent) return []
    return await listEscalatedByMe(200)
  }, [userPresent])

  // ── Numbered fetchers (page slice over the builder output) ─────────────────
  const fetchForApprovalsPaged = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<LeaveListItem>> => {
      const ownerHidden = await buildForApprovalsList()
      const start = (page - 1) * pageSize
      return { rows: ownerHidden.slice(start, start + pageSize), total: ownerHidden.length }
    },
    [buildForApprovalsList],
  )

  const fetchYouEscalatedPaged = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<LeaveListItem>> => {
      const all = await buildEscalatedList()
      const start = (page - 1) * pageSize
      return { rows: all.slice(start, start + pageSize), total: all.length }
    },
    [buildEscalatedList],
  )

  const pendingPaged = usePagedList<LeaveListItem>({
    queryKey: ['requests', 'for-approvals', scope, effectiveEmployeeId, effectiveRole],
    fetcher: fetchForApprovalsPaged,
  })

  const escalatedPaged = usePagedList<LeaveListItem>({
    queryKey: ['requests', 'you-escalated', effectiveEmployeeId, effectiveRole],
    fetcher: fetchYouEscalatedPaged,
  })

  // Visible rows + loading come straight from the numbered hook per tab.
  const pendingRows = pendingPaged.rows
  const escalatedRows = escalatedPaged.rows
  const pendingLoading = pendingPaged.isLoading
  const escalatedLoading = escalatedPaged.isLoading

  // ── History fetcher (server-backed) ───────────────────────────────────────
  // The History tab used to be in-session only, which meant any nav away
  // wiped past decisions. We now pull the manager's recently-decided leaves
  // (approved + rejected) from the server and merge with the in-session log
  // so freshly-clicked rows still flash up immediately under optimistic
  // mutation, while older rows survive a route change. `invalidate()` after
  // approve/reject already invalidates ['requests'], which triggers a refetch
  // here.
  const historyQuery = useQuery({
    queryKey: ['requests', 'history', scope, effectiveEmployeeId, effectiveRole],
    enabled: userPresent,
    queryFn: async (): Promise<ReadonlyArray<HistoryEntry>> => {
      const [approved, rejected] = await Promise.all([
        listLeaves({ scope, status: 'approved', limit: 50 }),
        listLeaves({ scope, status: 'rejected', limit: 50 }),
      ])
      const toEntry = (
        leave: LeaveListItem,
        decision: 'approved' | 'rejected',
      ): HistoryEntry => {
        const ts = leave.decidedAt ?? leave.updatedAt ?? leave.createdAt
        return { leave, decision, at: ts ? Date.parse(ts) : 0 }
      }
      const merged = [
        ...approved.map((l) => toEntry(l, 'approved')),
        ...rejected.map((l) => toEntry(l, 'rejected')),
      ]
      const ownerHidden = effectiveEmployeeId
        ? merged.filter((e) => e.leave.employee.id !== effectiveEmployeeId)
        : merged
      return ownerHidden.sort((a, b) => b.at - a.at)
    },
  })

  // Apply blackout filter when ?blackoutId= is present in the URL.
  const pendingList: ReadonlyArray<LeaveListItem> = blackoutContext
    ? pendingRows.filter((l) => blackoutContext.overlapIds.has(l.id))
    : pendingRows
  const escalatedList: ReadonlyArray<LeaveListItem> = blackoutContext
    ? escalatedRows.filter((l) => blackoutContext.overlapIds.has(l.id))
    : escalatedRows

  // Keep the single-fetcher alias for backwards-compat with conflict/pattern
  // logic below (was `list`).
  const list = pendingList

  // Tab label counts. For-approvals folds direct-pending + escalated-to-me
  // into one list, so its length is the "to action" count. You-escalated is
  // read-only (awaiting a higher decision), tracked separately.
  // The tab shows the full server total (the visible page is only one slice);
  // the blackout deep-link narrows to the matching page rows, so fall back to
  // the filtered list length there.
  const forApprovalsTabCount = blackoutContext ? pendingList.length : pendingPaged.total
  const youEscalatedTabCount = blackoutContext ? escalatedList.length : escalatedPaged.total

  // Decided log (approved + rejected) powers the History tab and its filter
  // chips. Computed once so the tab count and every chip read the same merged
  // source (in-session optimistic log + server history).
  const decidedEntries = mergeHistoryEntries(historyLog, historyQuery.data ?? [])
  const approvedEntries = decidedEntries.filter((e) => e.decision === 'approved')
  const rejectedEntries = decidedEntries.filter((e) => e.decision === 'rejected')
  const approvedTabCount = approvedEntries.length
  const rejectedTabCount = rejectedEntries.length
  const decidedTabCount = decidedEntries.length
  // The History rows currently shown, per the selected filter chip.
  const historyEntries =
    historyFilter === 'approved'
      ? approvedEntries
      : historyFilter === 'rejected'
        ? rejectedEntries
        : decidedEntries

  // Shared with the manager Home FocusCard so both surfaces mark the exact
  // same rows as conflicting. See lib/schedule-conflicts.ts.
  const conflictPeersMap = useMemo(() => computeConflictPeers(list), [list])

  // Flat Approvals table order: oldest submission first (first-come-first-serve).
  const pendingOldestFirst = useMemo<ReadonlyArray<LeaveListItem>>(
    () =>
      [...pendingList].sort((a, b) =>
        (a.createdAt ?? '').localeCompare(b.createdAt ?? ''),
      ),
    [pendingList],
  )

  // Decided leaves for the History tab, rendered through the same table. Status
  // is overridden to the recorded decision so the Status column reads
  // Approved/Rejected; deduped by leave id (optimistic log + server may repeat).
  const historyLeaves = useMemo<ReadonlyArray<LeaveListItem>>(() => {
    const seen = new Set<string>()
    const out: LeaveListItem[] = []
    for (const entry of historyEntries) {
      if (seen.has(entry.leave.id)) continue
      seen.add(entry.leave.id)
      out.push({ ...entry.leave, status: entry.decision })
    }
    return out
  }, [historyEntries])

  // History is an in-memory decided log (server rows + optimistic), so it
  // paginates client-side by slicing `historyLeaves` — same shape as the
  // escalated tab. The queryKey carries the filter + row count so switching
  // filter or a data change resets to page 1; paging itself does not re-slice
  // the whole set.
  const fetchHistoryPaged = useCallback(
    async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<LeaveListItem>> => {
      const start = (page - 1) * pageSize
      return { rows: historyLeaves.slice(start, start + pageSize), total: historyLeaves.length }
    },
    [historyLeaves],
  )
  const historyPaged = usePagedList<LeaveListItem>({
    queryKey: [
      'requests',
      'history',
      'paged',
      historyFilter,
      scope,
      effectiveEmployeeId,
      effectiveRole,
      historyLeaves.length,
    ],
    fetcher: fetchHistoryPaged,
  })

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['requests'] })
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    void queryClient.invalidateQueries({ queryKey: ['calendar'] })
    void queryClient.invalidateQueries({ queryKey: ['me'] })
    void queryClient.invalidateQueries({ queryKey: ['leaves'] })
    void queryClient.invalidateQueries({ queryKey: ['shell'] })
    void queryClient.invalidateQueries({ queryKey: ['nav-counts'] })
  }


  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => rejectLeave(id, reason),
    onError: (err: unknown) => {
      setRejectError(formatError(err))
    },
    onSuccess: () => {
      setRejectTarget(null)
      setRejectReason('')
      setRejectError(null)
      notify('Request rejected', 'warning')
    },
    onSettled: () => invalidate(),
  })

  // Approve a queue row. ``overCapacityAck`` is set only after the manager
  // confirms the over-capacity dialog. Without it, an over-cap approval 409s with
  // a snapshot — we open the confirm dialog rather than erroring, so the server
  // is the authoritative capacity gate. Returns whether the approval landed.
  const runApprove = async (
    leave: LeaveListItem,
    overCapacityAck: boolean,
  ): Promise<boolean> => {
    try {
      await approveLeave(
        leave.id,
        overCapacityAck && capacityNote.trim().length > 0
          ? capacityNote.trim()
          : undefined,
        overCapacityAck ? { overCapacityAck: true } : undefined,
      )
      setHistoryLog((prev) => [
        { leave, decision: 'approved', at: Date.now() },
        ...prev,
      ])
      notify(
        overCapacityAck ? 'Request approved above capacity' : 'Request approved',
        'success',
      )
      setCapacityConfirmLeave(null)
      setCapacityNote('')
      invalidate()
      return true
    } catch (err: unknown) {
      const snapshot = capacityAckRequired(err)
      if (snapshot && !overCapacityAck) {
        setCapacityConfirmLeave(leave)
        setCapacitySnapshot(snapshot)
        setCapacityConfirmError(null)
        return false
      }
      const message =
        err instanceof Error ? err.message : 'Failed to approve leave.'
      if (overCapacityAck) setCapacityConfirmError(message)
      else notify(message, 'error')
      return false
    }
  }

  const handleApprove = async (leave: LeaveListItem): Promise<void> => {
    await runApprove(leave, false)
  }

  const confirmApproveOverCapacity = async (): Promise<void> => {
    if (!capacityConfirmLeave) return
    if (capacityNote.trim().length === 0) {
      setCapacityConfirmError('A reason is required to approve above capacity.')
      return
    }
    setCapacityBusy(true)
    setCapacityConfirmError(null)
    try {
      await runApprove(capacityConfirmLeave, true)
    } finally {
      setCapacityBusy(false)
    }
  }

  const handleReject = (leave: LeaveListItem): void => {
    setRejectTarget(leave)
    setRejectReason('')
    setRejectError(null)
  }

  // ── Peak-conflict recommendation surface (RFC 005 K2) ───────────────────
  const conflictPairs = useMemo<ReadonlyArray<ConflictPair>>(() => {
    const pending = list.filter((l) => l.status === 'pending')
    return detectConflictPairs(pending)
  }, [list])

  const peakPeriodsQuery = useQuery({
    queryKey: ['peak-periods', 'active'],
    queryFn: () => listPeakPeriods('true'),
    enabled: conflictPairs.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const peakConflict = useManagerPeakConflict()
  // Which conflict cluster's "Ask ILMU" was last clicked. The recommendation
  // hook holds one result at a time, so its pending/data/error bind to the
  // active cluster only — every other cluster keeps its idle trigger.
  const [activeConflictKey, setActiveConflictKey] = useState<string | null>(null)




  // ── Conflict cluster split ──────────────────────────────────────────────
  // Overlapping pending rows are grouped by *transitive date overlap* via
  // `groupConflictClusters`, so a March overlap and a May overlap render
  // as TWO separate banners instead of one merged "Schedule conflict"
  // with a misleading combined date range. Each cluster keeps the
  // priority-desc order of its members (input order, since pendingList
  // is already sorted highest-priority-first above). Clusters themselves
  // appear in the same order as their best member.
  //
  // Every cluster gets an Ask ILMU trigger. Clusters whose window sits
  // inside an active peak period get the "Peak conflict · …" header (and
  // credit the festival factor); the rest get the plain "Schedule
  // conflict · …" header and a peak-less recommendation.
  const conflictGroups = useMemo<ReadonlyArray<ConflictGroup<LeaveListItem>>>(
    () => groupConflictClusters(pendingList),
    [pendingList],
  )
  // One descriptor per conflict cluster: a stable key, the askable pair
  // (top two cross-employee contenders), and the peak window behind the
  // clash if there is one. Every cluster is askable when it has a pair —
  // peak clusters credit the festival factor, plain overlaps don't.
  const clusterDescriptors = useMemo(() => {
    const periods = peakPeriodsQuery.data ?? []
    return conflictGroups.map((group, groupIndex) => {
      const key = `conflict-${group.windowStart}-${group.windowEnd}-${group.leaves[0]?.id ?? groupIndex}`
      const pair = pickClusterPair(group.leaves)
      const peak = pair ? pickPeakPeriodForPair(pair, periods) : null
      return { key, group, pair, peak }
    })
  }, [conflictGroups, peakPeriodsQuery.data])

  // Drop stale recommendation state when the active cluster leaves the queue
  // (e.g. one of its leaves was just approved) so a vanished collision can't
  // keep showing advice.
  useEffect(() => {
    if (
      activeConflictKey &&
      !clusterDescriptors.some((d) => d.key === activeConflictKey)
    ) {
      peakConflict.reset()
      setActiveConflictKey(null)
    }
    // peakConflict is a stable hook handle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterDescriptors, activeConflictKey])


  return (
    <>
      {/* ── Page — shared .page container (max-width + centred gutters), same
          as My Leaves and the other pages ──────────────────────────────── */}
      <div className="page" data-screen-label="Manager · Requests">
        {/* ── Between header: title+subtitle left / buttons right ─────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            // Match the header→content gap on the other pages (e.g. My Leaves
            // uses marginBottom: 22). Without it, dropping the subtitle left
            // the tab bar sitting too close to the title.
            marginBottom: 22,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                margin: 0,
                color: 'var(--text)',
              }}
            >
              Requests{' '}
              <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>
                · {forApprovalsTabCount} to action
              </span>
            </h1>
          </div>
        </div>

        {/* ── Tab bar — prototype .tab-bar (underline style) ─────────────── */}
        <div
          role="tablist"
          data-testid="tab-bar"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'flex', gap: 2 }}>
            <TabButton
              active={tab === 'for-approvals'}
              onClick={() => setTab('for-approvals')}
              testId="tab-for-approvals"
            >
              For approvals
              <TabCount n={forApprovalsTabCount} testId="tab-for-approvals-count" />
            </TabButton>
            {showHrDocReview ? (
              <TabButton
                active={tab === 'hr-doc-review'}
                onClick={() => setTab('hr-doc-review')}
                testId="tab-hr-doc-review"
              >
                HR Doc Review
                <TabCount n={hrReviewCount} testId="tab-hr-doc-review-count" />
              </TabButton>
            ) : null}
            <TabButton
              active={tab === 'you-escalated'}
              onClick={() => setTab('you-escalated')}
              testId="tab-you-escalated"
            >
              You escalated
              <TabCount n={youEscalatedTabCount} testId="tab-you-escalated-count" />
            </TabButton>
            <TabButton active={tab === 'history'} onClick={() => setTab('history')} testId="tab-history">
              History ({decidedTabCount})
            </TabButton>
          </div>
          {/* Outcome filter — top-right, only while on History. Frameless
              pills (no container padding) to stay sleek. */}
          {tab === 'history' ? (
            <div
              role="radiogroup"
              aria-label="Filter decisions by outcome"
              style={{ display: 'inline-flex', gap: 4, paddingBottom: 6 }}
            >
              {(
                [
                  { value: 'all', label: `All (${decidedTabCount})` },
                  { value: 'approved', label: `Approved (${approvedTabCount})` },
                  { value: 'rejected', label: `Rejected (${rejectedTabCount})` },
                ] as const
              ).map((opt) => {
                const active = historyFilter === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setHistoryFilter(opt.value)}
                    style={{
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: active ? 600 : 500,
                      padding: '5px 12px',
                      border: `1px solid ${active ? 'var(--text)' : 'var(--border)'}`,
                      borderRadius: 999,
                      background: active ? 'var(--text)' : 'transparent',
                      color: active ? 'var(--surface)' : 'var(--text-3)',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition:
                        'background 100ms ease, border-color 100ms ease, color 100ms ease',
                    }}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          ) : null}
        </div>

        {/* ── Blackout context banner (cross-tab) ────────────────────────── */}
        {blackoutContext ? (
          <FadeIn rise={6} style={{ marginBottom: 16 }}>
            <div data-testid="blackout-context-banner">
            <Banner
              kind="info"
              title={
                blackoutContext.meta
                  ? `Showing requests overlapping blackout: ${blackoutContext.meta.name}`
                  : 'Showing requests overlapping a blackout window'
              }
              subtitle={
                blackoutOverlapsQuery.isPending
                  ? 'Loading affected requests…'
                  : `${blackoutContext.overlapIds.size} request${blackoutContext.overlapIds.size === 1 ? '' : 's'} match this blackout.`
              }
              action={
                <button
                  type="button"
                  onClick={clearBlackoutFilter}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--steel-blue)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 13,
                  }}
                >
                  Clear filter
                </button>
              }
            />
            </div>
          </FadeIn>
        ) : null}

        {/* Tab panels fade in on switch (ui-ux.md rule 6 — show/hide motion). */}
        <FadeIn triggerKey={tab}>
        {/* ── For-approvals tab ───────────────────────────────────────────── */}
        {tab === 'for-approvals' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <section data-testid="requests-section">
              {pendingLoading ? (
                <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>
                  Loading…
                </div>
              ) : pendingList.length === 0 ? (
                <FadeIn triggerKey="for-approvals">
                  <Card padding={32}>
                    <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
                      Nothing waiting on you.
                    </div>
                  </Card>
                </FadeIn>
              ) : (
                <>
                  {/* One flat table, oldest submission first (first-come-
                      first-serve) — same styling as My Leave. Priority lanes
                      and conflict clusters are flattened; the per-row Conflict
                      badge preserves the overlap detail so no data is lost. */}
                  <ApprovalsTable
                    leaves={pendingOldestFirst}
                    onOpen={openLeaveDetail}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    conflictPeersByLeave={conflictPeersMap}
                    footer={
                      <PaginationControls
                        page={pendingPaged.page}
                        pageSize={pendingPaged.pageSize}
                        total={pendingPaged.total}
                        totalPages={pendingPaged.totalPages}
                        pageSizeOptions={pendingPaged.pageSizeOptions}
                        rangeStart={pendingPaged.rangeStart}
                        rangeEnd={pendingPaged.rangeEnd}
                        canPrev={pendingPaged.canPrev}
                        canNext={pendingPaged.canNext}
                        onPageChange={pendingPaged.setPage}
                        onPageSizeChange={pendingPaged.setPageSize}
                        isFetching={pendingPaged.isFetching}
                      />
                    }
                  />
                </>
              )}
            </section>
          </div>
        ) : null}

        {/* ── HR Doc Review tab (HR-only — verify the document, then forward) ── */}
        {tab === 'hr-doc-review' && showHrDocReview ? (
          <HrReviewTab onOpenLeave={openLeaveDetail} />
        ) : null}

        {/* ── You-escalated tab (read-only — a higher approver decides) ────── */}
        {tab === 'you-escalated' ? (
          <section data-testid="you-escalated-section">
            {escalatedLoading ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>
                Loading…
              </div>
            ) : escalatedList.length === 0 ? (
              <FadeIn triggerKey="you-escalated">
                <Card padding={32}>
                  <div style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 14 }}>
                    You haven&rsquo;t escalated anything. Leaves you forward up the
                    chain appear here until a higher approver decides them.
                  </div>
                </Card>
              </FadeIn>
            ) : (
              <>
                <ApprovalsTable
                  leaves={escalatedList}
                  onOpen={openLeaveDetail}
                  footer={
                    <PaginationControls
                      page={escalatedPaged.page}
                      pageSize={escalatedPaged.pageSize}
                      total={escalatedPaged.total}
                      totalPages={escalatedPaged.totalPages}
                      pageSizeOptions={escalatedPaged.pageSizeOptions}
                      rangeStart={escalatedPaged.rangeStart}
                      rangeEnd={escalatedPaged.rangeEnd}
                      canPrev={escalatedPaged.canPrev}
                      canNext={escalatedPaged.canNext}
                      onPageChange={escalatedPaged.setPage}
                      onPageSizeChange={escalatedPaged.setPageSize}
                      isFetching={escalatedPaged.isFetching}
                    />
                  }
                />
              </>
            )}
          </section>
        ) : null}

        {/* ── History tab — full decision log; outcome filter lives top-right ─ */}
        {tab === 'history' ? (
          historyLeaves.length === 0 ? (
            <div
              style={{
                padding: 24,
                color: 'var(--text-3)',
                textAlign: 'center',
                fontSize: 14,
              }}
            >
              {historyFilter === 'approved'
                ? 'Approved requests will appear here.'
                : historyFilter === 'rejected'
                  ? 'Rejected requests will appear here.'
                  : 'Approved and rejected requests you decided will appear here.'}
            </div>
          ) : (
            <ApprovalsTable
              leaves={historyPaged.rows}
              onOpen={openLeaveDetail}
              footer={
                <PaginationControls
                  page={historyPaged.page}
                  pageSize={historyPaged.pageSize}
                  total={historyPaged.total}
                  totalPages={historyPaged.totalPages}
                  pageSizeOptions={historyPaged.pageSizeOptions}
                  rangeStart={historyPaged.rangeStart}
                  rangeEnd={historyPaged.rangeEnd}
                  canPrev={historyPaged.canPrev}
                  canNext={historyPaged.canNext}
                  onPageChange={historyPaged.setPage}
                  onPageSizeChange={historyPaged.setPageSize}
                  isFetching={historyPaged.isFetching}
                />
              }
            />
          )
        ) : null}
        </FadeIn>
      </div>

      {/* ── Reject modal ─────────────────────────────────────────────────── */}
      <Modal
        open={Boolean(rejectTarget)}
        onClose={() => {
          setRejectTarget(null)
          setRejectError(null)
        }}
        title={rejectTarget ? `Reject — ${displayName(rejectTarget.employee)}` : ''}
      >
        {rejectTarget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 12, color: 'var(--text-2)', fontWeight: 600 }}>
              Reason (visible to employee)
            </label>
            <textarea
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="reject-reason"
              style={{
                padding: 10,
                border: '1px solid var(--border)',
                borderRadius: 8,
                background: 'var(--surface)',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
            {rejectError ? (
              <div
                role="alert"
                style={{
                  padding: '8px 10px',
                  background: 'var(--danger-bg)',
                  border: '1px solid var(--danger-line)',
                  borderRadius: 8,
                  color: 'var(--danger-fg)',
                  fontSize: 12,
                }}
              >
                {rejectError}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setRejectTarget(null)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text-2)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="confirm-reject"
                disabled={rejectReason.trim().length === 0 || rejectMutation.isPending}
                onClick={() => {
                  const reason = rejectReason.trim()
                  rejectMutation.mutate({ id: rejectTarget.id, reason })
                  setHistoryLog((prev) => [
                    { leave: rejectTarget, decision: 'rejected', at: Date.now() },
                    ...prev,
                  ])
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background:
                    rejectReason.trim().length === 0 ? 'var(--border-strong)' : 'var(--danger)',
                  color: 'var(--surface)',
                  fontWeight: 600,
                  cursor: rejectReason.trim().length === 0 ? 'not-allowed' : 'pointer',
                }}
              >
                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm reject'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* ── Over-capacity approve confirm ────────────────────────────────── */}
      <Modal
        open={Boolean(capacityConfirmLeave)}
        onClose={() => {
          setCapacityConfirmLeave(null)
          setCapacityNote('')
        }}
        title="Approve above team capacity?"
      >
        {capacityConfirmLeave ? (
          <div
            data-testid="capacity-confirm-modal"
            style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
          >
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-1)' }}>
              Approving{' '}
              <strong>{displayName(capacityConfirmLeave.employee)}</strong>&rsquo;s
              leave puts the team over its coverage cap for these dates. You can
              still approve — it will be recorded as an{' '}
              <strong>above-capacity approval</strong> and flagged for HR.
            </p>
            {capacitySnapshot ? (
              <div
                data-testid="capacity-confirm-snapshot"
                style={{
                  padding: '10px 12px',
                  background: 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                  fontSize: 13,
                  color: 'var(--text-1)',
                }}
              >
                <strong>
                  {capacitySnapshot.peakOthers + 1} of {capacitySnapshot.teamCap}
                </strong>{' '}
                · {capacitySnapshot.capPhrase}
              </div>
            ) : null}
            <div>
              <label
                htmlFor="manager-capacity-note"
                style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--text-2)',
                  marginBottom: 6,
                }}
              >
                Reason for approving above capacity{' '}
                <span style={{ color: 'var(--danger-fg)' }}>*</span>
              </label>
              <textarea
                id="manager-capacity-note"
                data-testid="capacity-confirm-note"
                value={capacityNote}
                onChange={(e) => setCapacityNote(e.target.value)}
                maxLength={500}
                rows={2}
                disabled={capacityBusy}
                placeholder="Required. Why are you approving above capacity? Recorded on the activity log."
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '8px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  color: 'var(--text-1)',
                  background: 'var(--surface-1, white)',
                }}
              />
            </div>
            {capacityConfirmError ? (
              <div
                role="alert"
                style={{
                  padding: '8px 10px',
                  background: 'var(--danger-bg)',
                  border: '1px solid var(--danger-line)',
                  borderRadius: 8,
                  color: 'var(--danger-fg)',
                  fontSize: 12,
                }}
              >
                {capacityConfirmError}
              </div>
            ) : null}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setCapacityConfirmLeave(null)
                  setCapacityNote('')
                }}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  background: 'var(--surface)',
                  color: 'var(--text-2)',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: 44,
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="capacity-confirm"
                disabled={capacityBusy || capacityNote.trim().length === 0}
                onClick={() => void confirmApproveOverCapacity()}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: 'var(--success)',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor:
                    capacityBusy || capacityNote.trim().length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  opacity:
                    capacityBusy || capacityNote.trim().length === 0 ? 0.6 : 1,
                  minHeight: 44,
                }}
              >
                {capacityBusy ? 'Approving…' : 'Approve above capacity'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* ── Escalate-to-HOD/Director modal ──────────────────────────────── */}
      {escalateTarget ? (
        <EscalateModal
          leaveId={escalateTarget.id}
          employeeName={displayName(escalateTarget.employee)}
          viewerRole={realRole}
          open={true}
          onClose={() => setEscalateTarget(null)}
          onSuccess={() => {
            notify(`Escalated · ${displayName(escalateTarget.employee)}`, 'success')
            invalidate()
          }}
        />
      ) : null}
    </>
  )
}

// ── Tab button (prototype .tab: underline, not pill) ──────────────────────────

interface TabButtonProps {
  active: boolean
  onClick: () => void
  testId: string
  children: ReactNode
}

/** Red count pill for a tab label. Renders nothing at 0 so a quiet tab stays
 *  quiet; a non-zero count reads as a call to action (ui-ux — actionable
 *  queues surface a badge). Not used on History, whose count is informational. */
function TabCount({ n, testId }: { n: number; testId?: string }): JSX.Element | null {
  if (n <= 0) return null
  return (
    <span
      data-testid={testId}
      style={{
        marginLeft: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        borderRadius: 999,
        background: 'var(--danger)',
        color: 'var(--surface)',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {n}
    </span>
  )
}

function TabButton({ active, onClick, testId, children }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      data-active={active ? 'true' : 'false'}
      onClick={onClick}
      style={{
        padding: '9px 14px',
        fontSize: 13,
        fontWeight: 500,
        color: active ? 'var(--text)' : 'var(--text-2)',
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        borderBottom: active
          ? '2px solid var(--space-cadet, var(--steel-blue))'
          : '2px solid transparent',
        marginBottom: -1,
        background: 'transparent',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

// ── Soft button (prototype .btn.btn-soft.btn-sm) ──────────────────────────────


// ── Utilities ─────────────────────────────────────────────────────────────────

/**
 * Combine the in-session history log (optimistic, freshly-clicked rows) with
 * the server-fetched recent decisions. Optimistic entries win on duplicate id
 * so a row that has just been approved/rejected in this tab doesn't blink
 * out and back in during the refetch. Sorted newest-first by `at`.
 */
function mergeHistoryEntries(
  local: ReadonlyArray<HistoryEntry>,
  server: ReadonlyArray<HistoryEntry>,
): ReadonlyArray<HistoryEntry> {
  const seen = new Set<string>()
  const merged: HistoryEntry[] = []
  for (const entry of local) {
    if (seen.has(entry.leave.id)) continue
    seen.add(entry.leave.id)
    merged.push(entry)
  }
  for (const entry of server) {
    if (seen.has(entry.leave.id)) continue
    seen.add(entry.leave.id)
    merged.push(entry)
  }
  return merged.sort((a, b) => b.at - a.at)
}

/**
 * First-come-first-serve comparator — the only pending-queue order. Oldest
 * submission first; `createdAt` is an ISO string, so a lexical compare is
 * chronological.
 *
 * There is deliberately no priority-score comparator. One existed behind
 * `LEAVE_PRIORITY_RANKING`, which never shipped ON; the business owner
 * confirmed FCFS as a hard rule on 2026-08-11 and both the flag and the
 * comparator were removed rather than left as a dormant alternative.
 */
export function comparePendingByOldest(a: LeaveListItem, b: LeaveListItem): number {
  const at = a.createdAt ?? ''
  const bt = b.createdAt ?? ''
  return at < bt ? -1 : at > bt ? 1 : 0
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`
  if (err instanceof Error) return err.message
  return 'Operation failed'
}

